import { createAction } from '@reduxjs/toolkit';
import type { Reducer } from '@reduxjs/toolkit';
import type { AppDispatch } from 'src/store/index';
import { authApi } from '@skynet/data-auth-api';
import { profileApi } from '@skynet/data-profile';
import { rolesApi } from '@skynet/data-roles';
import { signupApi } from '@skynet/data-signup';
import { tenantsApi } from '@skynet/data-tenants';
import { usersApi } from '@skynet/data-users';
import { exchangesApi } from '@skynet/data-exchanges';
import { dexColdWalletApi } from '@skynet/data-dex-cold-wallets';
import { dexTokenMapApi } from '@skynet/data-dex-token-map';
import { dexExchangesApi } from '@skynet/data-dex-exchanges';
import { dexTransactionsApi } from '@skynet/data-dex-transactions';
import { botCommandsApi } from '@skynet/data-bot-commands';
import { smmApi } from '@skynet/data-smm';
import { rtkDexApi } from 'src/store/DEX/rtkDexApi';
import { rtkExchangesApi } from 'src/store/shared/rtkExchangesApi';
import { marketingCampaignsApi } from '@skynet/data-marketing-campaigns';
import { bugReportsApi } from '@skynet/data-bug-reports';
import { itemsApi } from '@skynet/data-items';

export const resetStore = createAction('app/resetStore');

/**
 * Resets all RTK Query API caches.
 * Call this when switching tenants to clear stale data.
 */
export const resetAllRtkApiStates = (dispatch: AppDispatch) => {
  dispatch(authApi.util.resetApiState());
  dispatch(profileApi.util.resetApiState());
  dispatch(rolesApi.util.resetApiState());
  dispatch(signupApi.util.resetApiState());
  dispatch(tenantsApi.util.resetApiState());
  dispatch(usersApi.util.resetApiState());
  dispatch(exchangesApi.util.resetApiState());
  dispatch(dexColdWalletApi.util.resetApiState());
  dispatch(dexTokenMapApi.util.resetApiState());
  dispatch(botCommandsApi.util.resetApiState());
  dispatch(smmApi.util.resetApiState());
  dispatch(rtkDexApi.util.resetApiState());
  dispatch(dexExchangesApi.util.resetApiState());
  dispatch(dexTransactionsApi.util.resetApiState());
  dispatch(rtkExchangesApi.util.resetApiState());
  dispatch(marketingCampaignsApi.util.resetApiState());
  dispatch(bugReportsApi.util.resetApiState());
  dispatch(itemsApi.util.resetApiState());
};

/**
 * Resets tenant-scoped RTK Query API caches without touching user/profile data.
 * When switching tenants, we want to keep the user's auth and profile data intact while clearing tenant-specific data to avoid stale cache issues.
 */
export const resetTenantScopedApiStates = (dispatch: AppDispatch) => {
  dispatch(exchangesApi.util.resetApiState());
  dispatch(itemsApi.util.resetApiState());
  dispatch(dexColdWalletApi.util.resetApiState());
  dispatch(dexTokenMapApi.util.resetApiState());
  dispatch(botCommandsApi.util.resetApiState());
  dispatch(smmApi.util.resetApiState());
  dispatch(rtkDexApi.util.resetApiState());
  dispatch(dexExchangesApi.util.resetApiState());
  dispatch(dexTransactionsApi.util.resetApiState());
  dispatch(rtkExchangesApi.util.resetApiState());
  dispatch(marketingCampaignsApi.util.resetApiState());
  dispatch(bugReportsApi.util.resetApiState());
  dispatch(rolesApi.util.resetApiState());
  dispatch(tenantsApi.util.resetApiState());
  dispatch(usersApi.util.resetApiState());
};

export const withResetState = <T>(reducer: Reducer<T>, initialState: T): Reducer<T> => {
  return (state, action) => {
    if (action.type === resetStore.type) {
      return initialState;
    }
    return reducer(state, action);
  };
};
